import { getData } from 'clients/Clients';
import { getManufacturers } from 'clients/MasterDataClient';
import { getTopManufacturerList } from 'clients/WebServiceClient';
import { useCallback, useEffect } from 'react';
import { CACHE_KEY, deserializeFunc, getWithExpiry, serializeFunc, setWithExpiry } from 'utils/localStorageUtils';

const TTL = 24 * 60 * 60 * 1000; // 1 day in milliseconds

export function useManufacturers({ ttl = TTL } = {}) {
  const FIELDS = ['code', 'name', 'slug', 'shortName'];

  useEffect(() => {
    // Remove old localStorage data
    localStorage.removeItem('manufactuers');
  }, []);

  const getManufacturerByCode = useCallback(async (code) => {
    const data = await getManufacturerMapByCodes([code]);
    return Object.values(data)?.[0];
  }, []);

  const getManufacturerMapByCodes = useCallback(async (codes) => {
    const manufacturers = {};

    // Step 1: Try to get cached data
    const missingCodes = [];
    const cachedManufacturers = getWithExpiry(CACHE_KEY.MANUFACTURERS) || {};
    for (const code of codes) {
      if (cachedManufacturers[code]) {
        const manufacturer = deserializeFunc(cachedManufacturers[code], FIELDS);
        manufacturers[code] = manufacturer;
      } else {
        missingCodes.push(code);
      }
    }

    // Step 2: Fetch missing data, then cache
    if (missingCodes.length > 0) {
      const res = await getManufacturers({ codes: missingCodes, isAuth: false, isBasic: true });

      if (res.status !== 'OK') {
        console.error('[Error] Get manufacturers', res);
      } else {
        res.data.forEach((manufacturer) => {
          const serializedData = serializeFunc(manufacturer, FIELDS);
          cachedManufacturers[manufacturer.code] = serializedData;
          manufacturers[manufacturer.code] = deserializeFunc(serializedData, FIELDS);
        });

        // Update cache
        setWithExpiry(CACHE_KEY.MANUFACTURERS, cachedManufacturers, { ttl });
      }
    }

    return manufacturers;
  }, []);

  const getTopManufacturers = useCallback(async () => {
    let data = getWithExpiry(CACHE_KEY.TOP_MANUFACTURERS);

    if (!data) {
      const result = await getTopManufacturerList();
      data = getData(result) || [];

      if (data.length > 0) {
        setWithExpiry(CACHE_KEY.TOP_MANUFACTURERS, data, { ttl });
      }
    }

    return data;
  }, []);

  return { getManufacturerByCode, getManufacturerMapByCodes, getTopManufacturers };
}
